import _extends from "/usr/src/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import _defineProperty from "/usr/src/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/usr/src/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["src", "videoID", "loadAPI", "aspectRatio", "thumbnail", "plain", "borderRadius", "className", "style"];
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { PlayCircleFilled } from '@ant-design/icons';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getYoutubeVideoThumbnailSrc, parseYoutubeVideoID } from "../../utils/VideoUtils";

// Modify available aspect ratios here if needed in 'width:height' format.
const aspectRatios = ['3:2', '1:1', '4:3', '16:9', '9:16'];
const getAspectRatioPadding = aspectRatio => aspectRatio.split(':').reverse().reduce((height, width) => `${height / width * 100}%`);
const generatedAspectRatioPaddings = aspectRatios.reduce((acc, cur) => {
  acc[cur] = getAspectRatioPadding(cur);
  return acc;
}, {});

/**
 * https://developers.google.com/youtube/iframe_api_reference
 */

const loadYoutubeIframeAPI = () => {
  return new Promise(resolve => {
    if (window.YT) {
      return resolve();
    }
    const loadAPI = () => {
      window.onYouTubeIframeAPIReady = () => resolve();
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    };
    if (document.readyState === 'complete') {
      loadAPI();
    } else {
      window.addEventListener('load', () => loadAPI());
    }
  });
};
const DixuYoutubeVideo = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      src,
      videoID,
      loadAPI,
      aspectRatio,
      thumbnail,
      plain,
      borderRadius,
      className,
      style
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const classes = classNames('dixu-youtube-video', {
    [`border-radius-${borderRadius}`]: borderRadius
  }, className);
  const iframeRef = useRef();
  const playerRef = useRef();
  const {
    0: thumbnailSrc,
    1: setThumbnailSrc
  } = useState(thumbnail);
  const {
    0: thumbnailClicked,
    1: setThumbnailClicked
  } = useState(false);
  const {
    0: displayedSrc,
    1: setDisplayedSrc
  } = useState();
  const renderThumbnail = !thumbnailClicked;
  const renderIframe = displayedSrc && !renderThumbnail;
  const assignedStyle = _objectSpread({
    paddingTop: generatedAspectRatioPaddings[aspectRatio]
  }, style);
  const iframe = /*#__PURE__*/React.createElement("iframe", _extends({
    ref: iframeRef,
    className: plain ? className : 'dixu-youtube-video__player',
    title: "Youtube video",
    src: displayedSrc,
    allow: "encrypted-media",
    frameBorder: "0",
    allowFullScreen: "allowFullScreen",
    loading: "lazy",
    style: plain ? style : undefined
  }, plain ? undefined : rest));
  useEffect(() => {
    let parsedVideoID;
    if (videoID) {
      parsedVideoID = videoID;
    } else if (src) {
      parsedVideoID = parseYoutubeVideoID(src);
    }
    setDisplayedSrc(`https://www.youtube.com/embed/${parsedVideoID}?enablejsapi=1`);
  }, [src, videoID]);
  useEffect(() => {
    setThumbnailSrc(thumbnail);
  }, [thumbnail]);
  useEffect(() => {
    if (thumbnail) {
      return;
    }
    if (displayedSrc) {
      setThumbnailSrc(getYoutubeVideoThumbnailSrc(displayedSrc));
    } else {
      setThumbnailSrc(undefined);
    }
  }, [displayedSrc]);
  useEffect(() => {
    if (loadAPI && renderIframe) {
      loadYoutubeIframeAPI().then(() => {
        if (iframeRef.current) {
          playerRef.current = new window.YT.Player(iframeRef.current, {
            events: {
              onReady: event => {
                if (thumbnailClicked) {
                  event.target.playVideo();
                }
              }
            }
          });
        }
      });
    }
  }, [loadAPI, renderIframe, thumbnailClicked]);
  useImperativeHandle(ref, () => ({
    player: playerRef.current
  }), [playerRef.current]);
  if (plain) {
    return iframe;
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classes,
    style: assignedStyle
  }, rest), renderThumbnail && /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": "Play",
    className: "dixu-youtube-video__thumbnail",
    style: {
      backgroundImage: thumbnailSrc ? `url(${thumbnailSrc})` : undefined
    },
    onClick: () => setThumbnailClicked(true)
  }, /*#__PURE__*/React.createElement(PlayCircleFilled, null)), renderIframe && iframe);
});
DixuYoutubeVideo.defaultProps = {
  src: undefined,
  videoID: undefined,
  loadAPI: true,
  aspectRatio: '16:9',
  thumbnail: undefined,
  plain: false,
  borderRadius: undefined
};
export default DixuYoutubeVideo;